import * as XLSX from "xlsx";
import * as api from "./adminApi";
import { AdminShell, Card, useAsync } from "./components";

const COLUMNS = [
  "Name",
  "Email",
  "Organisation",
  "Course/Track",
  "Progress %",
  "Current Module",
  "Assessment Score",
  "Attempts",
  "Pass/Fail",
  "Completion Date",
  "Certificate ID",
] as const;

function toRow(r: api.LearnerProgressRow) {
  return {
    Name: r.learnerName,
    Email: r.email,
    Organisation: r.organisation,
    "Course/Track": `${r.programmeTitle} — ${r.trackTitle}`,
    "Progress %": r.progressPercent,
    "Current Module": r.currentModule ?? "—",
    "Assessment Score": r.assessmentScore ?? "—",
    Attempts: r.attempts,
    "Pass/Fail": r.passFail ?? "—",
    "Completion Date": r.completionDate ? new Date(r.completionDate).toLocaleDateString() : "—",
    "Certificate ID": r.certificateId ?? "—",
  };
}

function exportToExcel(rows: api.LearnerProgressRow[]) {
  const sheet = XLSX.utils.json_to_sheet(rows.map(toRow), { header: [...COLUMNS] });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Learner progress");
  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `learner-progress-${date}.xlsx`);
}

export function ReportsPage() {
  const { data: rows, error } = useAsync(api.getLearnerProgressReport, []);

  return (
    <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Learner progress report">
      {error && <Card>{error}</Card>}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ color: "#666", fontSize: "0.85rem", margin: 0 }}>
            {rows ? `${rows.length} enrolment(s)` : "Loading…"}
          </p>
          <button
            onClick={() => rows && exportToExcel(rows)}
            disabled={!rows || rows.length === 0}
            style={{
              padding: "0.4rem 0.9rem",
              borderRadius: 7,
              border: "none",
              background: !rows || rows.length === 0 ? "#9a9ab0" : "#1a1a2e",
              color: "#fff",
              cursor: !rows || rows.length === 0 ? "default" : "pointer",
              fontSize: "0.85rem",
            }}
          >
            Export to Excel
          </button>
        </div>

        {rows && rows.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: "0.8rem" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.82rem" }}>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c}
                      style={{
                        textAlign: "left",
                        padding: "0.4rem 0.6rem",
                        borderBottom: "2px solid #e6e6ee",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const cells = toRow(r);
                  return (
                    <tr key={r.enrollmentId}>
                      {COLUMNS.map((c) => (
                        <td
                          key={c}
                          style={{
                            padding: "0.4rem 0.6rem",
                            borderBottom: "1px solid #f0f0f4",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cells[c]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {rows && rows.length === 0 && <p style={{ color: "#666" }}>No enrolments yet.</p>}
      </Card>
    </AdminShell>
  );
}
